import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AttachmentsField } from "./AttachmentsField";
import { CategoryField } from "./CategoryField";

/**
 * Add / Edit dialog for any entity ledger.
 * Owns its own form state so re-opens always start clean.
 */
const SUPER_ROLES = ["super admin", "admin"];

export function EntityForm({ config, open, onOpenChange, editingRow, accounts, onSaved }) {
  const { currency } = useCurrency();
  const { user } = useAuth();
  const isSuper = SUPER_ROLES.includes(String(user?.role || "").toLowerCase());
  const [balanceReason, setBalanceReason] = useState("");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [refData, setRefData] = useState({});

  const editingId = editingRow?.id || null;

  useEffect(() => {
    if (!open) return;
    if (editingRow) {
      const init = {};
      config.fields.forEach((f) => {
        init[f.name] = editingRow[f.name] ?? "";
      });
      init.currency = editingRow.currency || currency;
      setForm(init);
    } else {
      setForm({ currency });
    }
  }, [open, editingRow, config.fields, currency]);

  useEffect(() => {
    if (!open) return;
    const refFields = config.fields.filter((f) => f.type === "entity-ref");
    if (!refFields.length) return;
    Promise.all(
      refFields.map((f) =>
        api.get(`/entities/${f.entity}`)
          .then(({ data }) => [f.name, Array.isArray(data) ? data : []])
          .catch(() => [f.name, []])
      )
    ).then((pairs) => setRefData(Object.fromEntries(pairs)));
  }, [open, config.fields]);

  const submit = async () => {
    const missing = config.fields.filter((f) => f.required && !form[f.name]);
    if (missing.length) {
      toast.error(`Please fill: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      config.fields.forEach((f) => {
        let v = form[f.name];
        if (f.type === "money" || f.type === "number") v = v === "" || v == null ? 0 : Number(v);
        if (f.type === "attachments") v = Array.isArray(v) ? v : [];
        payload[f.name] = v;
      });
      if (config.apiPath === "bank-accounts" && editingId) {
        const changed =
          Number(payload.balance || 0).toFixed(2) !== Number(editingRow?.balance || 0).toFixed(2);
        if (changed) {
          if (!isSuper) {
            toast.error(
              "The bank balance comes from the statement's closing balance. Only a super admin can override it."
            );
            setSaving(false);
            return;
          }
          if (balanceReason.trim().length < 5) {
            toast.error("Add a reason for overriding the bank balance");
            setSaving(false);
            return;
          }
          payload.balance_change_reason = balanceReason.trim();
        }
      }
      if (editingId) {
        await api.put(`/entities/${config.apiPath}/${editingId}`, payload);
        toast.success("Record updated");
      } else {
        await api.post(`/entities/${config.apiPath}`, payload);
        toast.success("Record added");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save record");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{editingId ? "Edit" : "Add"} {config.title}</DialogTitle>
          <DialogDescription>Enter the details below. Amounts are stored in the record&apos;s currency.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          {config.fields.map((f) => (
            <div key={f.name} className={(f.type === "attachments" || (f.type === "text" && f.name === "description")) ? "col-span-2" : ""}>
              <Label className="text-xs mb-1.5 block">
                {f.label} {f.required && <span className="text-danger">*</span>}
              </Label>
              {f.name === "category" && f.catType ? (
                <CategoryField
                  catType={f.catType}
                  value={form[f.name] || ""}
                  onChange={(v) => setForm((p) => ({ ...p, [f.name]: v }))}
                  testid={`field-${f.name}`}
                />
              ) : f.type === "select" ? (
                <Select value={form[f.name] || ""} onValueChange={(v) => setForm((p) => ({ ...p, [f.name]: v }))}>
                  <SelectTrigger data-testid={`field-${f.name}`}>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {form[f.name] && !f.options.includes(form[f.name]) && (
                      <SelectItem value={form[f.name]}>{form[f.name]}</SelectItem>
                    )}
                    {f.options.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "entity-ref" ? (
                (() => {
                  const opts = refData[f.name] || [];
                  const cur = form[f.name] ? String(form[f.name]) : "";
                  const hasCurrent = cur && opts.some((o) => String(o[f.optionValue]) === cur);
                  return (
                    <Select
                      value={cur}
                      onValueChange={(v) =>
                        setForm((p) => {
                          const next = { ...p, [f.name]: v };
                          const opt = opts.find((o) => String(o[f.optionValue]) === v);
                          if (opt && f.autofill) {
                            Object.entries(f.autofill).forEach(([target, src]) => {
                              next[target] = opt[src];
                            });
                          }
                          return next;
                        })
                      }
                    >
                      <SelectTrigger data-testid={`field-${f.name}`}>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {!hasCurrent && cur && <SelectItem value={cur}>{cur}</SelectItem>}
                        {opts.map((o) => (
                          <SelectItem key={o.id} value={String(o[f.optionValue])}>
                            {o[f.optionValue]}
                            {f.labelField && o[f.labelField] ? ` · ${o[f.labelField]}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                })()
              ) : f.type === "account" ? (
                <Select
                  value={form[f.name] || ""}
                  onValueChange={(v) => setForm((p) => ({ ...p, [f.name]: v === "__none__" ? "" : v }))}
                >
                  <SelectTrigger data-testid={`field-${f.name}`}>
                    <SelectValue placeholder="Select account (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}{a.bank ? ` · ${a.bank}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "combo" ? (
                <>
                  <Input
                    list={`dl-${f.name}`}
                    value={form[f.name] ?? ""}
                    onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
                    data-testid={`field-${f.name}`}
                    placeholder="Select or type…"
                  />
                  <datalist id={`dl-${f.name}`}>
                    {f.options.map((o) => (
                      <option key={o} value={o} />
                    ))}
                  </datalist>
                </>
              ) : f.type === "attachments" ? (
                <AttachmentsField
                  testid={`field-${f.name}`}
                  value={form[f.name]}
                  onChange={(v) => setForm((p) => ({ ...p, [f.name]: v }))}
                />
              ) : (
                <Input
                  type={f.type === "money" || f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                  step={f.type === "money" || f.type === "number" ? "any" : undefined}
                  value={form[f.name] ?? ""}
                  onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
                  disabled={
                    config.apiPath === "bank-accounts" &&
                    f.name === "balance" &&
                    !!editingId &&
                    !isSuper
                  }
                  title={
                    config.apiPath === "bank-accounts" && f.name === "balance" && editingId && !isSuper
                      ? "Set by the statement's closing balance — super admin only"
                      : undefined
                  }
                  data-testid={`field-${f.name}`}
                />
              )}
            </div>
          ))}
        </div>
        {config.apiPath === "bank-accounts" && editingId && (
          <div className="rounded-md border border-white/5 bg-white/[0.02] p-3 space-y-2">
            <p className="text-xs text-muted-foreground">
              The bank balance is maintained from each statement&apos;s closing balance.
              {isSuper
                ? " As super admin you may override it — a reason is logged."
                : " Only a super admin can override it."}
            </p>
            {isSuper && (
              <Input
                value={balanceReason}
                onChange={(e) => setBalanceReason(e.target.value)}
                placeholder="Reason for overriding the balance"
                data-testid="balance-reason-input"
              />
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="save-record-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} {editingId ? "Update" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
